import React, { useState, useRef, useEffect, useMemo } from "react";
import "./index.scss";

/**
 * SearchableDropDown
 *
 * Reusable dropdown component with an embedded search input in the menu.
 * Faithfully matches the reference design with floating outline label,
 * chevron indicator, dedicated search box, and smooth option selection.
 *
 * Props:
 *  - name        : field name
 *  - value       : currently selected value
 *  - onChange    : ({ target: { name, value } }) => void
 *  - label       : field label (e.g. "Brand Name", "State", "City")
 *  - options     : Array<{ value: string|number, label: string }> | Array<string>
 *  - placeholder : string (default: "Please Select [Label]")
 *  - error       : string error message
 *  - required    : boolean
 *  - disabled    : boolean
 *  - readOnly    : boolean
 *  - fullWidth   : boolean
 *  - className   : string
 *  - id          : string
 */
const SearchableDropDown = ({
  name,
  value = "",
  onChange,
  label,
  options = [],
  placeholder,
  error = "",
  required = false,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className = "",
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize options to [{ value, label }] array, filtering empty header items if any
  const normalizedOptions = useMemo(() => {
    return options
      .map((opt) =>
        typeof opt === "object" ? opt : { value: opt, label: opt },
      )
      .filter((opt) => opt && opt.value !== "");
  }, [options]);

  // Find currently selected option
  const selectedOption = normalizedOptions.find((opt) => {
    if (!value) return false;
    const optVal = String(opt.value).toLowerCase().trim();
    const curVal = String(value).toLowerCase().trim();
    if (optVal === curVal) return true;
    const optNorm = optVal.replace(/&/g, "and").replace(/\s+/g, " ");
    const curNorm = curVal.replace(/&/g, "and").replace(/\s+/g, " ");
    return optNorm === curNorm;
  });

  // Filter options based on search query
  const filteredOptions = normalizedOptions.filter((opt) => {
    const text = String(opt.label || opt.value || "").toLowerCase();
    return text.includes(searchTerm.trim().toLowerCase());
  });

  // Toggle dropdown open/close
  const handleToggle = () => {
    if (disabled || readOnly) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) setSearchTerm("");
      return next;
    });
  };

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Auto-focus the search input whenever the dropdown opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Keyboard navigation: Escape closes
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Option selection
  const handleSelectOption = (opt) => {
    if (onChange) {
      onChange({
        target: {
          name: name || id,
          value: opt.value,
        },
      });
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const defaultPlaceholder =
    placeholder || `Please Select ${label || ""}`.trim();
  const displayLabel = selectedOption
    ? selectedOption.label
    : value || defaultPlaceholder;
  const isSelected = Boolean(selectedOption || value);

  return (
    <div
      ref={containerRef}
      className={`searchable-dropdown-wrapper ${fullWidth ? "full-width" : ""} ${className}`}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`searchable-dropdown-control ${isOpen ? "is-open" : ""} ${
          error ? "has-error" : ""
        } ${disabled || readOnly ? "is-disabled" : ""}`}
        onClick={handleToggle}
        role="button"
        tabIndex={disabled || readOnly ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {label && (
          <label className="searchable-dropdown-floating-label">
            {label}
            {required && <span className="required-mark"> *</span>}
          </label>
        )}

        <div className="searchable-dropdown-value-container">
          <span
            className={`searchable-dropdown-display-text ${
              !isSelected ? "is-placeholder" : ""
            }`}
          >
            {displayLabel}
          </span>
        </div>

        <span
          className={`searchable-dropdown-chevron ${isOpen ? "is-open" : ""}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="searchable-dropdown-menu" role="listbox">
          <div className="searchable-dropdown-search-box">
            <input
              ref={searchInputRef}
              type="text"
              className="searchable-dropdown-search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="searchable-dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isItemActive =
                  String(opt.value).toLowerCase() ===
                  String(value).toLowerCase();
                return (
                  <div
                    key={opt.value}
                    className={`searchable-dropdown-item ${
                      isItemActive ? "is-active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOption(opt);
                    }}
                    role="option"
                    aria-selected={isItemActive}
                  >
                    {opt.label}
                  </div>
                );
              })
            ) : (
              <div className="searchable-dropdown-no-results">
                No results found
              </div>
            )}
          </div>
        </div>
      )}

      {error && <span className="input-error-msg">{error}</span>}
    </div>
  );
};

export default SearchableDropDown;
